/**
 * Purchase Detail Route Definition (Non-Lazy)
 *
 * Loader primes the purchase so intent preloads and deep links start the
 * fetch before this route's chunk even loads — previously the page fired two
 * client queries only after paint (the purchase plus the whole product
 * catalog, the latter now dropped in favor of the server-enriched
 * product_title).
 */
import { createFileRoute } from '@tanstack/react-router';
import { authenticatedGetShared } from '@/lib/api/authenticated-shared';
import { cacheFirst } from '@/lib/queries/cache-first';
import { PurchaseDetailTemplatePending } from '@/components/v2/page-template-pending';
import type { Purchase } from '@/lib/api/v2/market';

export const Route = createFileRoute('/_authenticated/profile/purchases/$purchaseId')({
  // Template-first pending while this route's chunk downloads. The page is
  // product-title lines over stacked info cards + a retry button — not the
  // generic media-hero shape.
  pendingComponent: PurchaseDetailTemplatePending,
  loader: ({ context, params }) => {
    const refresh = () =>
      context.queryClient.ensureQueryData({
        queryKey: ['market', 'purchase', params.purchaseId],
        queryFn: () =>
          authenticatedGetShared<Purchase>(`/v1/market/purchases/${params.purchaseId}`),
      });
    // Cache-first gate: re-entry (e.g. back from checkout) resolves from the
    // primed cache instantly — the page's usePurchase observes this same key
    // so no skeleton flashes — while the background refresh heals staleness;
    // a cold cache keeps the streamed fetch priming it.
    const { cached, stream } = cacheFirst(
      context.queryClient,
      ['market', 'purchase', params.purchaseId],
      refresh
    );
    return { purchasePromise: cached !== undefined ? Promise.resolve(cached) : stream() };
  },
});
